import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { getStyles } from "../index";
import InfoDrawer from "./InfoDrawer"; // Importera drawer-komponenten
import EditIcon from "@mui/icons-material/Edit";

const Books = ({ user }) => {
  const [roleplays, setRoleplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = getStyles(theme);

  useEffect(() => {
    // Inside your fetchRoleplays function, add the filter
    const fetchRoleplays = async () => {
      try {
        // Query teachers collection where type is "Rollspel"
        const teachersQuery = query(
          collection(db, "publicTeachers"),
          where("type", "==", "Bok"),
        );

        // Execute the query
        const querySnapshot = await getDocs(teachersQuery);

        // Map the results to an array of roleplay objects
        const roleplayList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter out items with characterName "HEJSAN"
        const filteredRoleplays = roleplayList.filter(
          (roleplay) =>
            roleplay.characterName !== "Ensam av August Strindberg" &&
            roleplay.characterName !== "Nietzsche",
          //roleplay.characterName !== "Nils Ferlin",
        );

        setRoleplays(filteredRoleplays);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching roleplays:", error);
        setLoading(false);
      }
    };
    fetchRoleplays();
  }, []);

  const handleRoleplayClick = (roleplayId) => {
    navigate(`/app/chat/${roleplayId}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{ ...styles.container, py: 0, width: { sm: `calc(100% - 400px)` } }}
      >
        <Box
          sx={{
            ...styles.innerContainer,
            width: { xs: "100%", md: "100%" },
          }}
        >
          <Typography
            variant="h1"
            sx={{ ...styles.header, borderBottom: "0px", mb: 4 }}
          >
            Chatta med böcker
          </Typography>

          {roleplays.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
              Inga böcker tillgängliga för tillfället.
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {roleplays.map((roleplay) => (
                <Grid item xs={12} sm={6} md={6} key={roleplay.id}>
                  <Box
                    onClick={() => handleRoleplayClick(roleplay.id)}
                    sx={{
                      height: 220,
                      borderRadius: "16px",
                      cursor: "pointer",
                      overflow: "hidden",
                      position: "relative",
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.02)",
                      },
                    }}
                  >
                    {user.admin && (
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          color: "white",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/larare/${roleplay.id}`);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {/* Background Image with Overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundImage: `url(${roleplay.backgroundImage || roleplay.imageUrl})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundColor: "rgba(0,0,0,0.5)", // Dark overlay
                        },
                      }}
                    />

                    {/* Content */}
                    <Box
                      sx={{
                        position: "relative",
                        zIndex: 1,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="h5"
                        component="div"
                        sx={{
                          color: "white",
                          fontWeight: "600",
                          fontSize: "1.7rem",
                          mb: 1,
                        }}
                      >
                        {roleplay.characterName}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="div"
                        sx={{
                          color: "white",
                          fontWeight: "500",
                          fontSize: "0.9rem",
                          mb: 1,
                        }}
                      >
                        {roleplay.cardDescription}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
              <Grid item xs={12} sm={6} md={6}>
                <Box
                  sx={{
                    height: 220,
                    borderRadius: "16px",
                    overflow: "hidden",
                    position: "relative",
                    backgroundColor: "rgba(0,0,0,0.9)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 3,
                  }}
                >
                  <Typography
                    variant="h5"
                    component="div"
                    sx={{
                      color: "white",
                      fontWeight: "600",
                      fontSize: "1.7rem",
                      mb: 2,
                      textAlign: "center",
                    }}
                  >
                    Fler böcker kommer snart
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "white",
                      textAlign: "center",
                      fontSize: "0.9rem",
                    }}
                  >
                    Vi håller på att träna vår AI-modell på många fler böcker,
                    ha tålamod :)
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>

      {/* Lägg till InfoDrawer-komponenten */}
      <InfoDrawer
        permanentlyOpen={true}
        open={true}
        drawerType="books"
        width="27%"
      />
    </>
  );
};

export default Books;
